import React, { useContext } from 'react';

import { Building, PieChart, Plus, User, Users } from 'lucide-react';

import { reloadCompany } from 'common/actions/company';
import { RevenueTimeframes } from 'common/company/RevenueHelpers';
import {
  AllCalculationFactors,
  CalculationCompanySpendFactors,
  type DefaultFactors,
} from 'common/constants/ideaFormulaFields';
import { AllUsersSegmentID } from 'common/constants/ideas';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { OpenModalContext } from 'common/containers/ModalContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import connect from 'common/core/connect';
import EditSegmentModal from 'common/modals/SavedSegmentsModal/EditSegmentModal';
import {
  CommonFactorOptions,
  getCalculationFunctionOptions,
} from 'common/subdomain/admin/v2/constants';
import SingleSelect from 'common/ui/SingleSelect';
import hasPermission from 'common/util/hasPermission';
import styles from 'css-module/components/subdomain/admin/v2/_AdminV2CustomIdeaFieldModal.module.scss';

import FormField from './FormField';

import type { Company, CustomField, Segment } from 'common/api/endpoints/companies';
import type {
  FormulaChangeFields,
  FormulaFieldFormState,
} from 'common/subdomain/admin/v2/hooks/useIdeaFieldForm';
import type { Option } from 'common/ui/common/select/SelectCommon';

type OwnProps = {
  formulaFieldState: FormulaFieldFormState;
  setValue: (field: FormulaChangeFields, value: string) => void;
  readOnly: boolean;
  openOverlayModal: () => void;
  closeOverlayModal: () => void;
};

type ConnectProps = {
  reloadCompany: () => void;
};

type Props = OwnProps & ConnectProps;

// Utils used to calculate the options for formula field dropdowns
const isNumericCustomField = (customField: CustomField) => customField.fieldType === 'number';

const calculateSegmentOptions = (segments: Segment[]): Option[] => {
  return segments
    .map((segment) => ({
      label: segment.name,
      value: segment._id,
      prefixIcon: PieChart,
    }))
    .concat({
      label: 'Everyone',
      value: AllUsersSegmentID,
      prefixIcon: Users,
    });
};

const calculateFactorAndFieldOptions = (company: Company): Option[] => {
  // TODO: add opportunity factors once we support those with Insights/Ideas
  const companyCustomFieldOptions: Option[] = company.customFields.company
    .filter(isNumericCustomField)
    .map((customField) => ({
      label: customField.name,
      value: customField._id,
      prefixIcon: Building,
    }));

  const userCustomFieldOptions: Option[] = company.customFields.user
    .filter(isNumericCustomField)
    .map((customField) => ({
      label: customField.name,
      value: customField._id,
      prefixIcon: User,
    }));
  const spendFactorOptions: (Option | null)[] = [
    company.hasCollectedMonthlySpend
      ? {
          label: 'Monthly Spend',
          value: CalculationCompanySpendFactors.monthlySpend,
          prefixIcon: Building,
        }
      : null,
    company.hasCollectedMonthlySpend && company.revenueTimeframe === RevenueTimeframes.arr
      ? {
          label: 'Annual Spend',
          value: CalculationCompanySpendFactors.annualSpend,
          prefixIcon: Building,
        }
      : null,
  ];

  return [
    ...CommonFactorOptions,
    ...companyCustomFieldOptions,
    ...userCustomFieldOptions,
    ...spendFactorOptions,
  ].filter(Boolean) as Option[];
};

function FormulaFieldInputs({
  formulaFieldState,
  setValue,
  readOnly = false,
  reloadCompany,
  openOverlayModal,
  closeOverlayModal,
}: Props) {
  const company = useContext(CompanyContext);
  const openModal = useContext(OpenModalContext);
  const viewer = useContext(ViewerContext);

  const factorAndFieldOptions = calculateFactorAndFieldOptions(company);
  const selectedFactorOrField = factorAndFieldOptions.find((option) =>
    [formulaFieldState.factor, formulaFieldState.fieldID].includes(option.value),
  );

  const segmentOptions = calculateSegmentOptions(company.segments);
  const selectedSegment = segmentOptions.find(
    (option) => option.value === formulaFieldState.segmentID,
  );

  const calculationFunctions = getCalculationFunctionOptions(formulaFieldState.factor);
  const selectedFunction = calculationFunctions.find(
    (option) => option.value === formulaFieldState.calculationFunction,
  );

  const handleCreateSegment = () => {
    // TODO: avoid using `openModal` and use a portal modal instead
    openOverlayModal();
    openModal(EditSegmentModal, {
      boards: [],
      onClose: () => {
        closeOverlayModal();
      },
      onUpdateQuery: () => {},
      reloadSegments: () => {},
      onSave: async (segment: Segment) => {
        await reloadCompany();
        setValue('segmentID', segment._id);
        closeOverlayModal();
      },
    });
  };

  // Handle dropdown selection - react-hook-form inspired approach
  const handleFactorOrFieldChange = (option?: Option) => {
    if (!option || readOnly) {
      return;
    }

    // Determine if the selected value is a factor or fieldID
    if (AllCalculationFactors.includes(option.value as DefaultFactors)) {
      setValue('factor', option.value);
      // Set calculation function if only one option is available
      const functions = getCalculationFunctionOptions(option.value);
      if (functions.length === 1) {
        setValue('calculationFunction', functions[0].value);
      }
    } else {
      setValue('fieldID', option.value);
    }
  };

  return (
    <FormField label="Options">
      <div className={styles.formulaSelects}>
        <SingleSelect
          allowClear={false}
          className={styles.fieldTypeSelect}
          disabled={readOnly}
          onChange={handleFactorOrFieldChange}
          options={factorAndFieldOptions}
          placeholder="Select factor or field"
          required
          value={selectedFactorOrField}
        />
        <SingleSelect
          allowClear={false}
          className={styles.fieldTypeSelect}
          cta={
            hasPermission('manageUserSegmentation', company, viewer)
              ? {
                  option: { label: 'Create new segment', value: 'newSegment', prefixIcon: Plus },
                  onAction: handleCreateSegment,
                }
              : undefined
          }
          disabled={readOnly}
          onChange={(option) => option && setValue('segmentID', option.value)}
          options={segmentOptions}
          placeholder="Select segment"
          required
          value={selectedSegment}
        />
        <SingleSelect
          allowClear={false}
          className={styles.fieldTypeSelect}
          disabled={readOnly || calculationFunctions.length === 1}
          onChange={(option) => option && setValue('calculationFunction', option.value)}
          options={calculationFunctions}
          placeholder="Select calculation"
          required
          value={selectedFunction}
        />
      </div>
    </FormField>
  );
}

// TODO: remove this once we extract the segments from the company object
export default connect(null, { reloadCompany })(
  FormulaFieldInputs,
) as unknown as React.FC<OwnProps>;
